import styled from 'styled-components';

import { Panel } from '../../../../components';
import { thinScrollbar } from '../../../../theme/mixins';
import { COLORS, HEADER_DESKTOP_PX, MOBILE, SPACE, STATUS_COLORS } from '../../../../theme/tokens';

/** Where the column is held on a wide desktop: under the studio's top bar and the area's pinned header, 24px down. */
const HELD_TOP = `calc(${HEADER_DESKTOP_PX}px + var(--ows-back-header-offset, 0px) + ${SPACE[24]})`;

/** The page tree, then Add route under it, framed as Game Lists frames its tree (the shared Panel at the column's
 *  tighter padding). On a wide desktop it is held in view as the editor scrolls, down to 24px above the screen's
 *  bottom, and scrolls on its own when the site has more routes than it holds. */
export const Root = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-width: 0;
  padding: ${SPACE[20]} ${SPACE[16]};
  position: sticky;
  top: ${HELD_TOP};
  max-height: calc(100vh - ${HELD_TOP} - ${SPACE[24]});
  overflow-y: auto;
  ${thinScrollbar}

  ${MOBILE} {
    position: static;
    max-height: none;
  }
`;

export const Marks = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  flex-shrink: 0;
`;

/* The unpublished-changes mark: the same publish-blue dot as the header's draft note. */
export const ChangedDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${COLORS.publishBlue};
`;

/** A keyword score's colour by tone (`scoreTone`), shared with the editor's keywords panel. */
export const SCORE_COLOR = { good: STATUS_COLORS.positive, fair: COLORS.warning, poor: COLORS.danger } as const;

/* The keyword score badge ("7/10"), toned like the editor's score. */
export const ScoreMark = styled.span<{ $tone: keyof typeof SCORE_COLOR }>`
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => SCORE_COLOR[$tone]};
`;

export const MissingMark = styled.span`
  display: inline-flex;
  color: ${COLORS.warning};
`;

export const ErrorMark = styled.span`
  display: inline-flex;
  color: ${COLORS.danger};
`;

/** Add route, in the column's footer: under the rows, always in view as they scroll (Game Lists' Create a new list). */
export const AddRow = styled.div`
  display: flex;
  flex: none;
  position: sticky;
  bottom: ${SPACE[0]};
  z-index: 2;
  /* The plain Panel's own fill, so rows scrolling under the pinned button do not show through it. */
  background: ${COLORS.surfaceCard};
`;
